/**
 * domains/market/engines/stateTransition/types.ts — MER Algo Footprints v7.1H
 *
 * State Transition Layer — types and contracts.
 *
 * This layer sits BETWEEN the analytical engines (SMC, Regime, Trap, Setup)
 * and the final UI/recommendation output. It converts analytical observations
 * into an explicit, finite market state with actionability classification.
 *
 * Design principles:
 *   - Deterministic and auditable — every state is explainable from rules
 *   - Price action primary — no state may override structure with gamma alone
 *   - Non-executing — output is decision-support only
 *   - Finite taxonomy — 10 named states, no prose-only classification
 */

// ─── State taxonomy ────────────────────────────────────────────────────────────

import type { ConfidenceBandLevel } from '../../../../shared/confidenceBand';

export type MarketState =
  | 'NO_VALID_SETUP'
  | 'COMPRESSION_BREAKOUT_PENDING'
  | 'PULLBACK_TO_SUPPORT_PENDING'
  | 'PULLBACK_TO_RESISTANCE_PENDING'
  | 'BREAKOUT_CONFIRMED_LONG'
  | 'BREAKDOWN_CONFIRMED_SHORT'
  | 'TRAP_REVERSAL_RISK'
  | 'RANGE_ROTATION'
  | 'TREND_CONTINUATION_ACTIVE'
  | 'EXHAUSTION_RISK';

export type Actionability = 'actionable' | 'pending' | 'avoid';

export type DirectionalBias = 'long' | 'short' | 'neutral';

// ─── Trigger conditions ───────────────────────────────────────────────────────

export interface StateTriggers {
  longTrigger?:   string;
  shortTrigger?:  string;
  confirmation?:  string[];
  failure?:       string[];
}

// ─── Scored signals used in state selection ───────────────────────────────────

export interface StateScoreFactors {
  // Structural clarity (0–100) — BOS/CHOCH, bias strength, displacement
  structuralClarity:    number;
  // Liquidity clarity (0–100) — equal highs/lows, sweep presence, zone proximity
  liquidityClarity:     number;
  // Trigger clarity (0–100) — defined entry level, setup maturity
  triggerClarity:       number;
  // Trap penalty (0–100 subtracted) — active trap severity
  trapPenalty:          number;
  // Extension penalty (0–100 subtracted) — trend maturity, exhaustion signals
  extensionPenalty:     number;
  // Gamma expansion modifier (0–20 added) — secondary, cannot override structure
  gammaExpansionBonus:  number;
  // Regime alignment (0–100) — dominant regime match
  regimeAlignment:      number;
  // Composite (0–100)
  composite:            number;
}

// ─── Full state transition result ─────────────────────────────────────────────

export interface StateTransitionResult {
  // Primary output
  state:            MarketState;
  actionability:    Actionability;
  directionalBias:  DirectionalBias;

  // Scores
  setupQuality:     number;   // 0–100 normalized
  confidence:       number;   // 0–100 normalized

  // P3 confidence penalties (D-P3AU). The governed OPERATIONAL confidence:
  // the 75/40 band of `confidence` above, lowered by the P3-D4
  // providerConfidence ceiling. A high `confidence` with a lower band is
  // correct, not contradictory — the number reports structural evidence
  // strength, the band reports how far that result can be relied upon.
  // Composed where provider evidence is in scope, so the engine stays
  // provider-blind; absent when it has not been composed.
  effectiveConfidenceBand?: ConfidenceBandLevel | null;

  // Explanation
  rationale:        string[];   // why this state was selected
  invalidation:     string[];   // conditions that would change the state
  triggers:         StateTriggers;
  contextFlags:     string[];   // observable conditions that contributed

  // Scoring breakdown (for audit / debugging)
  scoreFactors:     StateScoreFactors;

  // Human-readable operator guidance
  operatorGuidance: string;

  // Source — for auditability
  engineVersion:    string;
  evaluatedAt:      number;     // Unix ms
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const STATE_TRANSITION_ENGINE_VERSION = 'stateTransition-v1.0';

/** Minimum score threshold for ACTIONABLE states */
export const ACTIONABLE_MIN_COMPOSITE = 60;

/** Minimum score threshold for PENDING states */
export const PENDING_MIN_COMPOSITE = 35;

/** State action labels for UI */
export const STATE_ACTION_LABEL: Record<MarketState, string> = {
  NO_VALID_SETUP:                   'STAND ASIDE',
  COMPRESSION_BREAKOUT_PENDING:     'WAIT',
  PULLBACK_TO_SUPPORT_PENDING:      'WAIT',
  PULLBACK_TO_RESISTANCE_PENDING:   'WAIT',
  BREAKOUT_CONFIRMED_LONG:          'ACTIONABLE',
  BREAKDOWN_CONFIRMED_SHORT:        'ACTIONABLE',
  TRAP_REVERSAL_RISK:               'WAIT / HEDGE',
  RANGE_ROTATION:                   'WAIT',
  TREND_CONTINUATION_ACTIVE:        'ACTIONABLE',
  EXHAUSTION_RISK:                  'AVOID',
};

export const STATE_COLOR: Record<MarketState, string> = {
  NO_VALID_SETUP:                   '#3a4558',
  COMPRESSION_BREAKOUT_PENDING:     '#facc15',
  PULLBACK_TO_SUPPORT_PENDING:      '#60a5fa',
  PULLBACK_TO_RESISTANCE_PENDING:   '#f87171',
  BREAKOUT_CONFIRMED_LONG:          '#2ec4a9',
  BREAKDOWN_CONFIRMED_SHORT:        '#f0534e',
  TRAP_REVERSAL_RISK:               '#fb923c',
  RANGE_ROTATION:                   '#94a3b8',
  TREND_CONTINUATION_ACTIVE:        '#4ade80',
  EXHAUSTION_RISK:                  '#a78bfa',
};
